var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * CZNA-1563 — the change history of one `module_setting` key,
 * `GET /czna-authorities/module-settings/{key}/history` (CZNA-131), newest first.
 *
 * The backend has recorded every write since CZNA-131; nothing in the GUI read it,
 * so "who broke the form" had no answer short of SQL.
 *
 * <b>What a row holds.</b> Each row stores the value AFTER its change
 * (`ModuleSettingServiceImpl.writeHistory`). "What this change changed" is
 * therefore the diff from the next OLDER row to this one, and the oldest row
 * of the whole history has no predecessor at all. Pages come
 * {@link MODULE_SETTING_HISTORY_PAGE_SIZE} at a time; the caller pulls the next
 * page when it needs the predecessor of the last loaded row.
 *
 * <b>Cache key.</b> {@link moduleSettingHistoryQueryKey} extends
 * `moduleSettingQueryKey(key)`, deliberately: every write path invalidates that
 * key (`moduleSettingInvalidationKeys`), react-query matches invalidations by
 * prefix, and so every write — save, reset, rename, a drift decision, and the
 * rollback this ticket adds — refreshes an open history without each write
 * having to know the history exists.
 *
 * MEMORY rule: `useOkapiKy()` is unstable — captured in the query closure, never
 * listed in a dependency array.
 */
import { useMemo, useRef, useState } from 'react';
import { useOkapiKy } from '@folio/stripes/core';
import { useInfiniteQuery } from 'react-query';
import { moduleSettingQueryKey } from './useModuleSetting';
/** Wire values of `ModuleSettingChangeSource` (mod-czna-authorities). */
export var MODULE_SETTING_CHANGE_SOURCES = [
    'USER',
    'DEFAULT_UPDATE',
    'RESET',
    'DEFAULT_ACKNOWLEDGED',
    'DELETE',
];
/** Rows per request; the endpoint caps `limit` at 100. */
export var MODULE_SETTING_HISTORY_PAGE_SIZE = 20;
export var moduleSettingHistoryQueryKey = function (key) {
    return __spreadArray(__spreadArray([], moduleSettingQueryKey(key), true), ['history'], false);
};
var useModuleSettingHistory = function (key, _a) {
    var _b;
    var _c = _a === void 0 ? {} : _a, _d = _c.enabled, enabled = _d === void 0 ? true : _d;
    // Closure-capture only. MEMORY: ky is unstable — never in deps.
    var ky = useOkapiKy();
    /**
     * CZNA-1563 re-review — which failure is which. react-query 3 keeps one error
     * per query: a failed `fetchNextPage` and a failed refresh of the loaded pages
     * both leave `isError` with `data` kept, and whichever request succeeds next
     * clears it — so a next page arriving would wipe the news that the rows above
     * it are stale, and a stale list would read as "the next page failed".
     *
     * The page function itself knows which request it serves: the page index is
     * the page param, so a request for an index at or past the number of pages
     * already loaded is a NEXT page, anything below is a refresh of a loaded page
     * (page 0 always is — a first load is a refresh of nothing). Each kind sets
     * its own flag on failure and clears it on success. Both are scoped to the
     * key, so one template's failure never leaks into another's history.
     */
    var _e = useState(null), nextPageFailedKey = _e[0], setNextPageFailedKey = _e[1];
    var _f = useState(null), refetchFailedKey = _f[0], setRefetchFailedKey = _f[1];
    var loadedPagesRef = useRef(0);
    // CZNA-1563 re-review 3: requests outlive a key change. A request for the
    // template the dialog showed BEFORE settles into the same state setters, and
    // `setFlag(itsKey)` would overwrite the current template's flag. Only a
    // request for the current key may write a flag.
    var currentKeyRef = useRef(key);
    currentKeyRef.current = key;
    var _g = useInfiniteQuery(moduleSettingHistoryQueryKey(key), function (_a) { return __awaiter(void 0, [_a], void 0, function (_b) {
        var page, isNextPage, setFlag, body, err_1;
        var _c = _b.pageParam, pageParam = _c === void 0 ? 0 : _c;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    page = pageParam;
                    isNextPage = page > 0 && page >= loadedPagesRef.current;
                    setFlag = isNextPage ? setNextPageFailedKey : setRefetchFailedKey;
                    _d.label = 1;
                case 1:
                    _d.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, ky
                            .get("czna-authorities/module-settings/".concat(key, "/history"), {
                            searchParams: { page: page, limit: MODULE_SETTING_HISTORY_PAGE_SIZE },
                        })
                            .json()];
                case 2:
                    body = _d.sent();
                    if (currentKeyRef.current === key)
                        setFlag(function (k) { return (k === key ? null : k); });
                    return [2 /*return*/, __assign(__assign({}, body), { page: page })];
                case 3:
                    err_1 = _d.sent();
                    if (currentKeyRef.current === key)
                        setFlag(key);
                    throw err_1;
                case 4: return [2 /*return*/];
            }
        });
    }); }, {
        enabled: enabled && Boolean(key),
        staleTime: 0,
        retry: false,
        getNextPageParam: function (lastPage, pages) {
            var _a, _b;
            var loaded = pages.reduce(function (sum, p) { var _a, _b; return sum + ((_b = (_a = p.moduleSettingHistory) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0); }, 0);
            // An empty page ends paging even if totalRecords says otherwise — rows
            // can vanish between requests only by a bug, but looping on it would be
            // worse than showing a short list.
            return ((_b = (_a = lastPage.moduleSettingHistory) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0 && loaded < lastPage.totalRecords
                ? lastPage.page + 1
                : undefined;
        },
    }), data = _g.data, isLoading = _g.isLoading, isError = _g.isError, isFetching = _g.isFetching, hasNextPage = _g.hasNextPage, isFetchingNextPage = _g.isFetchingNextPage, fetchNextPage = _g.fetchNextPage, refetch = _g.refetch;
    var entries = useMemo(function () {
        var _a;
        // A write between two page requests shifts every later row by one, so the
        // next page can start with a row already shown. Keep the first copy.
        var seen = new Set();
        return ((_a = data === null || data === void 0 ? void 0 : data.pages) !== null && _a !== void 0 ? _a : [])
            .flatMap(function (p) { var _a; return (_a = p.moduleSettingHistory) !== null && _a !== void 0 ? _a : []; })
            .filter(function (e) {
            if (seen.has(e.id))
                return false;
            seen.add(e.id);
            return true;
        });
    }, [data]);
    var pages = (_b = data === null || data === void 0 ? void 0 : data.pages) !== null && _b !== void 0 ? _b : [];
    loadedPagesRef.current = pages.length;
    return {
        entries: entries,
        totalRecords: pages.length > 0 ? pages[pages.length - 1].totalRecords : 0,
        isLoading: isLoading,
        isError: isError,
        hasNextPage: Boolean(hasNextPage),
        isFetchingNextPage: isFetchingNextPage,
        isFetchNextPageError: nextPageFailedKey === key,
        // Only meaningful with rows on screen; a first load that failed is `isError`.
        isRefetchError: refetchFailedKey === key && pages.length > 0,
        isRefreshing: isFetching && !isFetchingNextPage,
        fetchNextPage: function (options) { fetchNextPage(options); },
        refetch: function () { refetch(); },
    };
};
export default useModuleSettingHistory;
